import React, { useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../services/api';
import CaptchaWidget from '../components/CaptchaWidget';
import { PillInput, PillButton } from '../components/PillKit';
import { CaretLeft } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) return setError('Password must be at least 6 characters');
    if (username && !/^[a-zA-Z0-9_]{3,20}$/.test(username))
      return setError('Username must be 3–20 letters, numbers, or underscores');
    if (!captchaToken) return setError('Please complete the CAPTCHA challenge.');

    setLoading(true);
    try {
      const payload = { email, password, name, username: username || undefined, captcha_token: captchaToken };
      if (referralCode) payload.referral_code = referralCode;
      await register(payload);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err));
      captchaRef.current?.reset();
      setCaptchaToken(null);
    } finally { setLoading(false); }
  };

  const handleGoogleSignup = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <header className="px-5 pt-6 pb-2 flex items-center justify-between">
        <Link to="/" className="text-white p-1" aria-label="Back">
          <CaretLeft className="w-6 h-6" />
        </Link>
        <Link to="/"><img src={LOGO_URL} alt="Divispend" className="h-10 w-auto" /></Link>
        <span className="w-6" />
      </header>

      <main className="flex-1 max-w-md w-full mx-auto px-6 pt-4 pb-12">
        <div className="mb-8">
          <h1 className="text-4xl font-extrabold tracking-tight text-white leading-none" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Create Account
          </h1>
          <p className="text-[#22C55E] text-xl font-bold mt-1.5" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Start your hustle in 30 seconds
          </p>
        </div>

        {referralCode && (
          <div className="mb-4 p-3 bg-[#22C55E]/10 border-[2px] border-[#22C55E]/40 rounded-2xl text-[#22C55E] text-sm font-semibold">
            Referral applied: <code className="font-mono">{referralCode}</code>
          </div>
        )}

        {error && <div className="mb-4 p-3 bg-red-500/10 border-[2px] border-red-500/40 rounded-2xl text-red-400 text-sm" data-testid="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="text-xs tracking-[0.18em] uppercase font-bold text-white/60 mb-2 block">Full Name</label>
            <PillInput
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              autoComplete="name"
              required
              data-testid="register-name-input"
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.18em] uppercase font-bold text-white/60 mb-2 block">Username (Optional)</label>
            <div className="relative">
              <span className="absolute left-6 top-1/2 -translate-y-1/2 text-white/40 text-lg font-bold z-10">$</span>
              <PillInput
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                placeholder="yourhandle"
                className="pl-12"
                autoComplete="username"
                data-testid="register-username-input"
              />
            </div>
            {username && /^[a-zA-Z0-9_]{3,20}$/.test(username) && (
              <p className="text-xs text-white/40 mt-2">divispend.com/{username}</p>
            )}
          </div>
          <div>
            <label className="text-xs tracking-[0.18em] uppercase font-bold text-white/60 mb-2 block">Email</label>
            <PillInput
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
              data-testid="register-email-input"
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.18em] uppercase font-bold text-white/60 mb-2 block">Password</label>
            <PillInput
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              autoComplete="new-password"
              required
              data-testid="register-password-input"
            />
          </div>

          <CaptchaWidget ref={captchaRef} onVerify={(t) => setCaptchaToken(t)} />

          <PillButton onClick={handleSubmit} disabled={loading || !captchaToken} data-testid="register-submit-button">
            {loading ? 'Creating account…' : 'Sign Up Now'}
          </PillButton>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-white/40 text-xs uppercase tracking-widest">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <button
          type="button"
          onClick={handleGoogleSignup}
          className="w-full bg-white text-black rounded-full px-6 py-4 text-base font-bold tracking-tight hover:bg-white/90 flex items-center justify-center gap-3"
          data-testid="google-signup-button"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continue with Google
        </button>

        <p className="mt-6 text-center text-xs text-white/50">
          By signing up, you agree to our{' '}
          <Link to="/privacy_policy" className="text-[#22C55E] hover:underline">Terms & Privacy Policy</Link>.
        </p>

        <p className="mt-6 text-center text-sm text-white/60">
          Already have an account?{' '}
          <Link to="/login" className="text-[#22C55E] font-bold hover:underline" data-testid="register-login-link">
            Sign in
          </Link>
        </p>
      </main>
    </div>
  );
}
